// =============================================================================
// brokerSowPdf.ts — Insurance broker site SOW summary (T-INS-SOW)
// =============================================================================
// James, 31 Aug 2026: "they like a quick site SOW to review our coverage but
// it's time consuming; if the details are here may as well make it quick."
// v2, 4 Sep smoke: "a bit much — slim it down with some summaries… more things
// like site address, lat and long, an option to put a snip / photo of the
// design, high level build plan or a gantt program snip."
//
// So: a FACTS PANEL (straight from project data — site, address, coordinates,
// client, structure, dates, crew), an optional design snip image, five SHORT
// AI-drafted summaries (edited by a human first), and a high-level program
// table. One page, Coretel design, forwarded to the broker manually.
// =============================================================================

import {
  getPdfMake, fetchLogoDataUrl, PDF_COLOR as C, PDF_COMPANY as CO,
} from "@/features/quotes/quotePdf";

export interface BrokerSowSections {
  works_description: string;
  heights_and_access: string;
  plant_and_lifting: string;
  subcontractors: string;
  key_hazards_and_controls: string;
  contract_value_band?: string;
}

export interface BrokerSowFact {
  label: string;
  value: string;
}

export interface BrokerSowProgramRow {
  name: string;
  start: string | null;   // yyyy-mm-dd
  end: string | null;
  isKey: boolean;
}

export interface BrokerSowOpts {
  projectCode: string;
  projectName: string;
  facts: BrokerSowFact[];                 // pre-filtered: only filled values
  sections: BrokerSowSections;
  program: BrokerSowProgramRow[];         // empty array = omit the block
  imageDataUrl?: string | null;           // design snip / cover photo
  preparedByName: string;
}

const SECTION_ORDER: { key: keyof BrokerSowSections; label: string }[] = [
  { key: "works_description",        label: "Works summary" },
  { key: "heights_and_access",       label: "Heights & access" },
  { key: "plant_and_lifting",        label: "Plant & lifting" },
  { key: "subcontractors",           label: "Subcontractors" },
  { key: "key_hazards_and_controls", label: "Key hazards & controls" },
  { key: "contract_value_band",      label: "Contract value band" },
];

function d(iso: string | null): string {
  if (!iso) return "—";
  const dt = new Date(iso + "T00:00:00");
  return isNaN(dt.getTime()) ? iso : dt.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}

export async function generateBrokerSowPdf(opts: BrokerSowOpts): Promise<void> {
  const pdfMake = await getPdfMake();
  const logo = await fetchLogoDataUrl("/coretel-logo-full.png");
  const today = new Date().toLocaleDateString("en-AU", { day: "2-digit", month: "long", year: "numeric" });

  // Facts panel: label/value pairs, two per row.
  const factRows: any[] = [];
  for (let i = 0; i < opts.facts.length; i += 2) {
    const pair = [opts.facts[i], opts.facts[i + 1]];
    factRows.push(pair.flatMap(f => f ? ([
      { text: f.label.toUpperCase(), fontSize: 7, bold: true, color: C.primary, characterSpacing: 0.8, margin: [0, 3, 0, 0] },
      { text: f.value, fontSize: 9, color: C.text, margin: [0, 3, 8, 0] },
    ]) : [{ text: "" }, { text: "" }]));
  }

  const sectionBlocks = SECTION_ORDER
    .filter(({ key }) => (opts.sections[key] ?? "").trim() !== "")
    .flatMap(({ key, label }) => ([
      { text: label.toUpperCase(), fontSize: 8, bold: true, color: C.primary, characterSpacing: 1, margin: [0, 10, 0, 3] },
      { text: String(opts.sections[key]).trim(), fontSize: 9.5, color: C.text, lineHeight: 1.22 },
    ]));

  const programBlock = opts.program.length === 0 ? [] : [
    { text: "HIGH-LEVEL PROGRAM", fontSize: 8, bold: true, color: C.primary, characterSpacing: 1, margin: [0, 12, 0, 4] },
    {
      table: {
        widths: ["*", 78, 78],
        body: [
          [
            { text: "Activity", fontSize: 7.5, bold: true, color: C.muted },
            { text: "Start", fontSize: 7.5, bold: true, color: C.muted },
            { text: "Finish", fontSize: 7.5, bold: true, color: C.muted },
          ],
          ...opts.program.map(r => ([
            { text: (r.isKey ? "★ " : "") + r.name, fontSize: 8.5, color: C.text },
            { text: d(r.start), fontSize: 8.5, color: C.text },
            { text: d(r.end), fontSize: 8.5, color: C.text },
          ])),
        ],
      },
      layout: {
        hLineWidth: (i: number) => (i <= 1 ? 0.75 : 0.4),
        vLineWidth: () => 0,
        hLineColor: () => "#e3ddd4",
        paddingTop: () => 2.5, paddingBottom: () => 2.5,
        paddingLeft: () => 0, paddingRight: () => 6,
      },
    },
  ];

  const doc: any = {
    pageSize: "A4",
    pageMargins: [45, 52, 45, 58],
    footer: (page: number, pages: number) => ({
      columns: [
        { text: `${CO.name}  ·  ABN ${CO.abn}`, fontSize: 7.5, color: C.dim },
        { text: `Prepared for insurance broker review  ·  ${today}`, fontSize: 7.5, color: C.dim, alignment: "center" },
        { text: `Page ${page} of ${pages}`, fontSize: 7.5, color: C.dim, alignment: "right" },
      ],
      margin: [45, 20, 45, 0],
    }),
    content: [
      {
        columns: [
          logo ? { image: logo, width: 110 } : { text: CO.name, fontSize: 14, bold: true, color: C.primary },
          {
            text: "SITE SCOPE OF WORKS\nINSURANCE SUMMARY",
            alignment: "right", fontSize: 14, bold: true, color: C.primary,
            characterSpacing: 1.5, margin: [0, 4, 0, 0],
          },
        ],
      },
      { text: opts.projectCode ? `${opts.projectCode} · ${opts.projectName}` : opts.projectName,
        fontSize: 16, bold: true, color: C.ink, margin: [0, 20, 0, 2] },
      { canvas: [{ type: "line", x1: 0, y1: 0, x2: 60, y2: 0, lineWidth: 2, lineColor: C.primary }], margin: [0, 6, 0, 4] },

      // Facts panel — the details a broker scans first, straight from Ops data
      {
        table: { widths: [95, "*", 95, "*"], body: factRows },
        layout: "noBorders",
        margin: [0, 4, 0, 0],
      },
      {
        columns: [
          { text: "PREPARED BY", fontSize: 7, bold: true, color: C.primary, characterSpacing: 0.8, width: 95, margin: [0, 3, 0, 0] },
          { text: `${opts.preparedByName} · ${CO.name}`, fontSize: 9, color: C.text, margin: [0, 3, 0, 0] },
        ],
      },

      // Optional design snip / cover photo
      ...(opts.imageDataUrl ? [{
        image: opts.imageDataUrl,
        fit: [500, 200],
        alignment: "center",
        margin: [0, 12, 0, 0],
      }, {
        text: "Design reference", fontSize: 7.5, color: C.dim, alignment: "center", margin: [0, 2, 0, 0],
      }] : []),

      ...sectionBlocks,
      ...programBlock,

      {
        text: "This summary is provided to assist with insurance coverage review. Detailed safe work method statements, lift plans and permits are held by Coretel Group and available on request.",
        fontSize: 8, color: C.muted, italics: true, margin: [0, 14, 0, 0],
      },
    ].filter(Boolean),
  };

  pdfMake.createPdf(doc).download(
    `BrokerSOW_${(opts.projectCode || opts.projectName).replace(/[^\w-]+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`,
  );
}
